(() => {
  'use strict';

  const controllers = new WeakMap();

  const createReaderController = (reader) => {
    if (!reader || controllers.has(reader)) return;

    const header = reader.querySelector('[data-dotd-reader-header]');
    const progress = reader.querySelector('[data-dotd-reader-progress]');

    let lastScrollY = window.scrollY;
    let frame = null;

    const update = () => {
      frame = null;

      const scrollY = window.scrollY;
      const maximum =
        document.documentElement.scrollHeight - window.innerHeight;

      const ratio =
        maximum > 0
          ? Math.min(1, Math.max(0, scrollY / maximum))
          : 0;

      if (progress) {
        progress.style.transform = `scaleY(${ratio})`;
      }

      if (header) {
        const movingDown = scrollY > lastScrollY;
        const beyondHero = scrollY > window.innerHeight * 0.35;

        header.classList.toggle(
          'is-hidden',
          movingDown && beyondHero,
        );
      }

      lastScrollY = scrollY;
    };

    const requestUpdate = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, {
      passive: true,
    });

    window.addEventListener('resize', requestUpdate);

    update();

    controllers.set(reader, {
      destroy() {
        window.removeEventListener('scroll', requestUpdate);
        window.removeEventListener('resize', requestUpdate);

        if (frame !== null) {
          window.cancelAnimationFrame(frame);
        }

        controllers.delete(reader);
      },
    });
  };

  const initialize = (scope = document) => {
    const readers = [];

    if (scope?.matches?.('[data-dotd-webtoon-reader]')) {
      readers.push(scope);
    }

    scope
      ?.querySelectorAll?.('[data-dotd-webtoon-reader]')
      .forEach((reader) => readers.push(reader));

    readers.forEach(createReaderController);
  };

  const destroy = (scope) => {
    const readers = [];

    if (scope?.matches?.('[data-dotd-webtoon-reader]')) {
      readers.push(scope);
    }

    scope
      ?.querySelectorAll?.('[data-dotd-webtoon-reader]')
      .forEach((reader) => readers.push(reader));

    readers.forEach((reader) => {
      controllers.get(reader)?.destroy();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize(),
      { once: true },
    );
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });
})();

/* DOTD GA4 reader events v1 - reuse the Google & YouTube tag; no page_view/config. */
(() => {
  'use strict';
  const match = location.pathname.match(/^\/pages\/dotd-webtoon-episode-(\d+)\/?$/);
  const reader = document.querySelector('[data-dotd-webtoon-reader]');
  if (!match || !reader || window.Shopify?.designMode) return;
  const episode = 'ep' + match[1].padStart(2, '0');
  const sent = new Set();
  const loadedAt = Date.now();
  let started = false;
  let privacyRequested = false;
  const allowed = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch (_) { return false; }
  };
  const send = (name) => {
    if (sent.has(name) || !allowed() || document.visibilityState !== 'visible') return false;
    const tag = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
    if (!tag) return false;
    const params = {
      send_to: 'G-12L8RB065J',
      episode_id: episode,
      content_type: 'webtoon',
      elapsed_seconds: Math.round((Date.now() - loadedAt) / 1000),
      transport_type: 'beacon'
    };
    if (typeof window.gtag === 'function') window.gtag('event', name, params);
    else {
      window.dataLayer = window.dataLayer || [];
      (function () { window.dataLayer.push(arguments); })('event', name, params);
    }
    sent.add(name);
    return true;
  };
  const first = reader.querySelector('.dotd-webtoon-reader__page');
  const end = reader.querySelector('.dotd-webtoon-reader__ending-label');
  const images = reader.querySelectorAll('.dotd-webtoon-reader__image');
  const lastImage = images[images.length - 1];
  let firstVisible = false;
  let endVisible = false;
  let firstTimer = null;
  let endTimer = null;
  const check = () => {
    if (!allowed() || document.visibilityState !== 'visible') return;
    send('webtoon_open');
    if (firstVisible && !started && !firstTimer) {
      firstTimer = setTimeout(() => {
        firstTimer = null;
        if (firstVisible && send('webtoon_read_start')) started = true;
        checkEnd();
      }, 1000);
    }
    checkEnd();
  };
  const checkEnd = () => {
    if (!started || !endVisible || sent.has('webtoon_end_reached') || endTimer) return;
    endTimer = setTimeout(() => {
      endTimer = null;
      if (endVisible && lastImage?.complete && lastImage.naturalHeight > 0) {
        send('webtoon_end_reached');
      }
    }, 1000);
  };
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === first) {
          firstVisible = entry.isIntersecting;
          if (!firstVisible && firstTimer) { clearTimeout(firstTimer); firstTimer = null; }
        }
        if (entry.target === end) {
          endVisible = entry.isIntersecting;
          if (!endVisible && endTimer) { clearTimeout(endTimer); endTimer = null; }
        }
      });
      check();
    }, { threshold: 0 });
    if (first) observer.observe(first);
    if (end) observer.observe(end);
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      clearTimeout(firstTimer); firstTimer = null;
      clearTimeout(endTimer); endTimer = null;
    } else check();
  });
  document.addEventListener('visitorConsentCollected', check);
  lastImage?.addEventListener('load', checkEnd);
  const prepare = () => {
    if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures && !privacyRequested) {
      privacyRequested = true;
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) check();
      });
    }
    check();
  };
  prepare();
  let attempts = 0;
  const readyTimer = setInterval(() => {
    prepare();
    if (++attempts >= 30 || sent.has('webtoon_open')) clearInterval(readyTimer);
  }, 1000);
})();
